import React, { useEffect, useState } from 'react';
import { Card, Container, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, FormGroup } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { getData, postData } from '../../../CommonElements/Functions/PostGetFun';
import { Image } from '../../../AbstractElements';
import { dynamicImage } from '../../../Utils';
import { toast } from 'react-toastify';
import CardHeaderSpan from '../../../Utils/CommonComponents/CardHeaderSpan';
import { API } from '../../../api/API';

const LeaveDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state || {}) as any;
  const empId = state.empId ?? state.EmployeeId ?? null;
  const name = (state.name ?? `${state.FirstName || ''} ${state.LastName || ''}`).trim();

  const serverUrl = localStorage.getItem('serverUrl') ;
  const [loading, setLoading] = useState<boolean>(true);
  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [totalLeaveCount, setTotalLeaveCount] = useState<number | null>(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'cancel' | null>(null);
  const [selectedReq, setSelectedReq] = useState<any>(null);
  const [rejectRemark, setRejectRemark] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [leaveStatusMap, setLeaveStatusMap] = useState<Record<string, string>>({}); // code -> label

  const formatDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const formatDateToDDMMMYYYY = (dateStr?: string) => {
    if (!dateStr) return '-';
    // some APIs already return yyyy-mm-dd, Date can parse it
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
  };

  useEffect(() => {
    // fetch leave status mapping once
    const fetchLeaveStatusMap = async () => {
      if (!serverUrl) return;
      try {
        const url = `${serverUrl}${API.getLeaveStatus}`;
        const { status, data } = await getData(url, true);
        const payload = data?.data ?? data;
        if (status === 200 && payload ) {
          // payload example: { Submit: "0", Raised: "1", Approve: "2" }
          const map: Record<string, string> = {};
          Object.entries(payload).forEach(([k, v]) => {
            map[String(v)] = k; // invert to code => label
          });
          setLeaveStatusMap(map);
        }
      } catch (err) {
        console.error('Failed fetching leave status map', err);
      }
    };

    fetchLeaveStatusMap();

    if (!empId) {
      setLoading(false);
      return;
    }

    const fetchLeaves = async () => {
      setLoading(true);
      try {
        // determine date range
        let from_date: string;
        let to_date: string;

        // 1) explicit dates passed via location.state
        if (state.from_date && state.to_date) {
          from_date = state.from_date;
          to_date = state.to_date;
        } else if (state.fullYear) {
          // 2) full calendar year (e.g. 2025-01-01 to 2026-01-01)
          const year = new Date().getFullYear();
          from_date = `${year}-01-01`;
          to_date = `${year + 1}-01-01`;
        } else {
          // 3) default: one year back -> today
          const to = new Date();
          const from = new Date();
          from.setFullYear(from.getFullYear() - 1);
          // use full date for range
          from_date = formatDate(new Date(from.getFullYear(), from.getMonth(), from.getDate()));
          to_date = formatDate(to);
        }

        const url = `${serverUrl}${API.getEmployeeLeave}`;
        const { status, data } = await getData(url, true, {
          employee_id: empId,
          from_date,
          to_date,
        });
        // Normalize: some endpoints return { statusCode, data }
        const payload = data?.data ?? data;
        // console.log('data from API', payload);

        if (status === 200 && Array.isArray(payload.LeaveRequest)) {
          setLeaveRequests(payload.LeaveRequest);
          setTotalLeaveCount(payload.TotalLeaveCount ?? null);
        } else {
          setLeaveRequests([]);
          setTotalLeaveCount(null);
        }
      } catch (err) {
        console.error('Failed fetching employee leave', err);
        setLeaveRequests([]);
        setTotalLeaveCount(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empId]);

  const openActionModal = (type: 'approve' | 'reject' | 'cancel', req: any) => {
    setActionType(type);
    setSelectedReq(req);
    setRejectRemark('');
    setActionModalOpen(true);
  };

  const closeActionModal = () => {
    setActionModalOpen(false);
    setActionType(null);
    setSelectedReq(null);
    setRejectRemark('');
  };

  const handleConfirmAction = async () => {
    if (!selectedReq || !actionType) return;
    if (!serverUrl) {
      toast.error('Server URL not configured.');
      return;
    }

    setActionLoading(true);
    try {
      if (actionType === 'approve') {
        const url = `${serverUrl}${API.leaveApprove}`;
        const { status, data } = await postData(url, true, { leave__request_id: selectedReq.LeaveReqId });
        const ok = status === 200 || data?.statusCode === 200;
        if (ok) {
          toast.success(data?.message || 'Leave approved');
        } else {
          toast.error(data?.message || 'Approve failed');
        }
      } else if (actionType === 'reject' || actionType === 'cancel') {
        // both reject and cancel use the same API and require a remark
        if (!rejectRemark || !rejectRemark.trim()) {
          toast.error('Please provide a remark.');
          setActionLoading(false);
          return;
        }
        const url = `${serverUrl}${API.leaveReject}`;
        const { status, data } = await postData(url, true, { leave__request_id: selectedReq.LeaveReqId, leave_reject_remark: rejectRemark });
        const ok = status === 200 || data?.statusCode === 200;
        if (ok) {
          toast.success(data?.message || (actionType === 'cancel' ? 'Leave cancelled' : 'Leave rejected'));
        } else {
          toast.error(data?.message || (actionType === 'cancel' ? 'Cancel failed' : 'Reject failed'));
        }
      }
      // refresh
      await (async () => {
        setLoading(true);
        try {
          const to = new Date();
          const from = new Date();
          from.setMonth(from.getMonth() - 1);
          const from_date = formatDate(new Date(from.getFullYear(), from.getMonth(), 1));
          const to_date = formatDate(to);
          const url = `${serverUrl}${API.getEmployeeLeave}`;
          const { status, data } = await getData(url, true, { employee_id: empId, from_date, to_date });
          const payload = data?.data ?? data;
          if (status === 200 && Array.isArray(payload.LeaveRequest)) {
            setLeaveRequests(payload.LeaveRequest);
            setTotalLeaveCount(payload.TotalLeaveCount ?? null);
          } else {
            setLeaveRequests([]);
            setTotalLeaveCount(null);
          }
        } catch (err) {
          console.error('Failed fetching employee leave', err);
        } finally {
          setLoading(false);
        }
      })();
    } catch (err) {
      console.error('Action failed', err);
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
      closeActionModal();
    }
  };

  return (
    <div className="page-body p-2">
      <Container fluid>
        <Card className="">
          <CardHeaderSpan headingClassName='pb-0' back={true} heading={`Leave from ${name}`} span={'ReportListTitle'} />

          <div style={{ marginBottom: 12 }} className='p-2'>
            {/* <strong>Employee:</strong> {name || '-'} &nbsp; <strong>Employee ID:</strong> {empId ?? '-'} */}
          </div>

          {loading ? (
            <div className="text-center py-3">
              <div>
                <Image
                  className="b-r-10 img-80"
                  src={dynamicImage('dashboard/loader.gif')}
                  alt="Loading..."
                />
              </div>
            </div>
          ) : (
            <div className='px-2' style={{ display: 'flex', gap: 12, flexWrap: 'wrap', }}>
              {leaveRequests.length === 0 && <div>No leave requests in the selected range.</div>}

              {leaveRequests.map((req: any) => (
                <div key={req.LeaveReqId} style={{ minWidth: 320, flex: '1 0 320px' }}>
                  <Card className="p-3" style={{ height: '85%' }}>
                    {/* Row 1: LeaveType and LeaveDays */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <div style={{ fontWeight: 'bold' }}>{req.LeaveType || '-'}</div>
                      <div> {req.LeaveDays ?? '-'} Full days</div>
                    </div> 

                    {/* Row 2: From, To, Status */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                      <div>{formatDateToDDMMMYYYY(req.LeaveFrom)} to {formatDateToDDMMMYYYY(req.LeaveTo)}</div>
                      <div
                        style={{
                          padding: '4px 8px',
                          borderRadius: 4,
                          backgroundColor:
                            leaveStatusMap[String(req.LeaveStatus)]?.toLowerCase() === 'raised'
                              ? '#e96900'
                              : leaveStatusMap[String(req.LeaveStatus)]?.toLowerCase() === 'approve'
                              ? '#6cbb33'
                              : leaveStatusMap[String(req.LeaveStatus)]?.toLowerCase() === 'reject'
                              ? '#cf0b0b'
                              : leaveStatusMap[String(req.LeaveStatus)]?.toLowerCase() === 'cancel'
                              ? 'red'
                              : '#f8d7da',
                          color: '#fff',
                        }}
                      >
                        {leaveStatusMap[String(req.LeaveStatus)] ?? req.LeaveStatus ?? '-'}
                      </div>
                    </div>

                    {/* Row 3: Remark (LeaveReason) */}
                    <div style={{ marginBottom: 3 }}>
                      {req.LeaveReason || '-'}
                    </div>

                    {/* Row 4: LeaveRejectRemark */}
                    <div style={{ marginBottom: 3 }}>
                      Manager: {req.LeaveRejectRemark || ''}
                    </div>

                    {/* Row 5: Attachment */}
                    <div style={{ marginBottom: 5 }}>
                      {req.Attachment ? (
                        <a href={req.Attachment.startsWith('http') ? req.Attachment : `${serverUrl}media?id=${req.Attachment}`} target="_blank" rel="noreferrer">Attachment</a>
                      ) : ''}
                    </div>

                    {/* Row 6: Actions */}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 0 }}>
                      {leaveStatusMap[String(req.LeaveStatus)]?.toLowerCase() === 'reject' ? null : (
                        leaveStatusMap[String(req.LeaveStatus)]?.toLowerCase() === 'approve' ? (
                          <button 
                            style={{ backgroundColor: '#e96900', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px' }}
                            onClick={() => openActionModal('cancel', req)}
                          >
                            Cancel Request
                          </button>
                        ) : (
                          <>
                            <button 
                              style={{ backgroundColor: '#1C65D3', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px' }}
                              onClick={() => openActionModal('approve', req)}
                            >
                              Approve Leave
                            </button>
                            <button 
                              style={{ backgroundColor: '#cf0b0b', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px' }}
                              onClick={() => openActionModal('reject', req)}
                            >
                              Reject Leave
                            </button>
                          </>
                        )
                      )}
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </Card>
      </Container>

      {/* Action modal */}
      <Modal isOpen={actionModalOpen} toggle={closeActionModal}>
        <ModalHeader toggle={closeActionModal}>{actionType === 'approve' ? 'Confirm Approve' : 'Reject Leave'}</ModalHeader>
        <ModalBody>
          {actionType === 'reject' || actionType === 'cancel' ? (
            <FormGroup>
              <label>{actionType === 'reject' ? 'Reject Remark' : 'Cancel Remark'}</label>
              <Input type="textarea" value={rejectRemark} onChange={(e) => setRejectRemark(e.target.value)} />
            </FormGroup>
          ) : (
            <p>Are you sure you want to approve this leave request?</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color={actionType === 'approve' ? 'primary' : actionType === 'cancel' ? 'warning' : 'danger'} onClick={handleConfirmAction} disabled={actionLoading}>
            {actionLoading ? 'Please wait...' : (actionType === 'approve' ? 'Yes' : actionType === 'cancel' ? 'Cancel Leave' : 'Reject')}
          </Button>
          <Button color="secondary" onClick={closeActionModal} disabled={actionLoading}>No</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default LeaveDashboard;
